<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, type LocationQueryRaw } from 'vue-router'
import { NButton } from 'naive-ui'
import { useMemoryEntriesStore, GLOBAL_PROJECT, type MemoryEntry } from '../stores/memory_entries'
import MemoryScopeNav, { type MemoryScopeLink } from './MemoryScopeNav.vue'
import MemoryEntryList from './MemoryEntryList.vue'
import MemoryEntryDrawer from './MemoryEntryDrawer.vue'
import {
  ALL_SCOPES,
  ALL_TYPES,
  filterMemoryEntries,
  entriesInScope,
  memoryEntryKey,
  memoryEntryPath,
  memoryEntryTypes,
  type MemorySort,
} from './MemoryEntryMeta'

// The Memory browser frame both memory routes share: a scope column, an
// entries column and a detail column (the default slot). /memory fills
// the slot with a placeholder; /memory/:project/:name with the entry.
//
// The scope comes from the URL (`?scope=`, else the open entry's scope,
// else every scope) so scope links are plain links. The type filter and
// text filter are local, seeded from `?type=` / `?q=`, and every link the
// browser renders carries them — so the list doesn't reset when the
// route swaps /memory for an entry page and this frame remounts.
//
// Below xl the entry page hides the scope + list columns and shows a
// back link instead, so a phone reader lands on the entry, not the list.

const props = defineProps<{
  selected?: { project: string; name: string } | null
}>()

defineSlots<{ default: () => unknown }>()

const store = useMemoryEntriesStore()
const route = useRoute()

onMounted(() => {
  if (!store.loaded) void store.fetch()
})

function queryParam(key: string): string | null {
  const value = route.query[key]
  return typeof value === 'string' && value !== '' ? value : null
}

const scope = computed(() => queryParam('scope') ?? props.selected?.project ?? ALL_SCOPES)
const type = ref(queryParam('type') ?? ALL_TYPES)
const query = ref(queryParam('q') ?? '')
const sort = ref<MemorySort>('updated')

// A type that only existed in the previous scope would filter to nothing.
watch(scope, () => {
  type.value = ALL_TYPES
})

const scopeEntries = computed(() => entriesInScope(store.entries, scope.value))
const types = computed(() => memoryEntryTypes(scopeEntries.value))
const visible = computed(() =>
  filterMemoryEntries(store.entries, { scope: scope.value, type: type.value, query: query.value, sort: sort.value }),
)
const selectedKey = computed(() => (props.selected ? memoryEntryKey(props.selected) : null))

function carriedQuery(scopeParam: string | null): LocationQueryRaw {
  const out: LocationQueryRaw = {}
  if (scopeParam) out.scope = scopeParam
  if (type.value !== ALL_TYPES) out.type = type.value
  if (query.value.trim()) out.q = query.value.trim()
  return out
}

// An entry page defaults to the entry's own scope, so `?scope=` is only
// needed when the list is showing a different one (e.g. "all").
function entryLink(entry: MemoryEntry) {
  return { path: memoryEntryPath(entry), query: carriedQuery(scope.value === entry.project ? null : scope.value) }
}

function scopeLink(slug: string) {
  const out: LocationQueryRaw = {}
  if (slug !== ALL_SCOPES) out.scope = slug
  if (query.value.trim()) out.q = query.value.trim()
  return { path: '/memory', query: out }
}

const scopeLinks = computed<MemoryScopeLink[]>(() => [
  { slug: ALL_SCOPES, count: store.totalCount, to: scopeLink(ALL_SCOPES) },
  ...store.groups.map((g) => ({ slug: g.project, count: g.count, to: scopeLink(g.project) })),
])

const backLink = computed(() => scopeLink(scope.value))

// Scopes offered in the New entry drawer: `global` always, plus every
// scope that currently holds entries.
const drawerShow = ref(false)
const scopeSlugs = computed(() => [...new Set([GLOBAL_PROJECT, ...store.groups.map((g) => g.project)])])
</script>

<template>
  <div class="flex flex-col gap-5 pt-2 pb-8">
    <header class="flex flex-wrap items-end gap-3">
      <div class="flex min-w-[220px] flex-1 flex-col gap-1">
        <h1 class="page-title">Memory</h1>
        <p
          v-if="store.totalCount > 0"
          data-testid="memory-summary"
          class="m-0 text-sm tabular-nums text-ink-muted"
          :class="{ 'max-xl:hidden': selected }"
        >
          <span class="font-medium text-ink">{{ store.globalCount }}</span> global<!--
          --><template v-if="store.projectScopeCount > 0">
            ·
            <span class="font-medium text-ink">{{ store.projectCount }}</span>
            project-specific across
            <span class="font-medium text-ink">{{ store.projectScopeCount }}</span>
            project{{ store.projectScopeCount === 1 ? '' : 's' }}
          </template>
        </p>
      </div>
      <div v-if="store.loaded" class="flex shrink-0 items-center gap-2" :class="{ 'max-xl:hidden': selected }">
        <button
          type="button"
          data-testid="memory-refresh"
          class="min-h-10 rounded-[10px] border border-border-control px-4 text-sm text-ink transition-colors hover:bg-surface-hover disabled:opacity-60 max-sm:min-h-11"
          :disabled="store.loading"
          @click="store.fetch()"
        >
          {{ store.loading ? 'Refreshing…' : 'Refresh' }}
        </button>
        <NButton
          type="primary"
          class="!min-h-10 max-sm:!min-h-11"
          data-testid="memory-new-entry"
          @click="drawerShow = true"
        >
          + New entry
        </NButton>
      </div>
    </header>

    <RouterLink
      v-if="selected"
      :to="backLink"
      class="inline-flex min-h-11 items-center self-start text-sm text-ink-muted no-underline hover:text-ink xl:hidden"
    >
      ← All memory
    </RouterLink>

    <div
      class="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(260px,340px)_minmax(0,1fr)] xl:grid-rows-[auto_1fr] 2xl:grid-cols-[190px_minmax(280px,360px)_minmax(0,1fr)] 2xl:grid-rows-1"
    >
      <MemoryScopeNav
        :scopes="scopeLinks"
        :active="scope"
        class="xl:col-start-1 xl:row-start-1 2xl:border-r 2xl:border-border-divider 2xl:pr-3"
        :class="{ 'max-xl:hidden': selected }"
      />
      <MemoryEntryList
        v-model:type="type"
        v-model:query="query"
        v-model:sort="sort"
        :entries="visible"
        :types="types"
        :selected-key="selectedKey"
        :show-scope="scope === ALL_SCOPES"
        :link-for="entryLink"
        class="xl:col-start-1 xl:row-start-2 2xl:col-start-2 2xl:row-start-1 2xl:border-r 2xl:border-border-divider 2xl:pr-4"
        :class="{ 'max-xl:hidden': selected }"
      />
      <div class="min-w-0 xl:col-start-2 xl:row-span-2 xl:row-start-1 2xl:col-start-3 2xl:row-span-1">
        <slot />
      </div>
    </div>

    <MemoryEntryDrawer v-model:show="drawerShow" :projects="scopeSlugs" />
  </div>
</template>
